<script lang="ts">
  import { navigate } from 'svelte-routing';
  import Bars from '../charts/Bars.svelte';
  import Chart from '../charts/Chart.svelte';
  import Readout from '../charts/Readout.svelte';
  import { measureAxis, type TileId, type TileView } from '../../utils/insights';
  import { locale, t } from '../../i18n/index.ts';

  // TileChart is a tile's bar a day (spec 006 FR-079) in the shared chart
  // language (spec 010 FR-033). Hovering or tapping a day reads it, and
  // a tapped day's button opens it: Health's day for steps, the training
  // log's through onday for the rest. Exercise days are yes or no, so they
  // have no value scale.
  let { id, v, onday }: { id: TileId; v: TileView; onday: (day: string) => void } = $props();

  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let shown = $derived(hover ?? at);

  let axis = $derived(id === 'exercise_days' ? null : measureAxis(id));
  let scale = $derived(axis ? axis.scale(Math.max(0, ...v.bars) * axis.factor) : { lo: 0, hi: 1, ticks: [], step: 1 });
  let n = $derived(v.bars.length || 1);
  let positions = $derived(v.bars.map((_, i) => (i + 0.5) / n));
  const dayFormat = new Intl.DateTimeFormat(locale(), { weekday: 'short', month: 'short', day: 'numeric' });
  const dayName = (d: string) => dayFormat.format(new Date(`${d}T12:00:00`));
  let reading = $derived(shown != null ? t('activitiesDayValue', { day: dayName(v.days[shown]), value: v.format(v.bars[shown]) }) : '');

  function open(day: string) {
    if (id === 'steps') navigate(`/activities/health?date=${day}`);
    else onday(day);
  }
</script>

<Readout title={reading || t('activitiesHoverDay')} idle={!reading} items={[]}>
  {#snippet action()}
    {#if at != null}
      <button class="btn btn-secondary btn-sm" onclick={() => open(v.days[at!])}>{id === 'steps' ? t('activitiesOpenOnHealth') : t('activitiesOpenInLog')}</button>
    {/if}
  {/snippet}
</Readout>
<Chart lo={scale.lo} hi={scale.hi} unit={axis?.unit} yTicks={axis ? scale.ticks.map((tick) => ({ v: tick, text: axis!.tick(tick, scale.step) })) : []}
  xTicks={v.labels.map((l, i) => ({ at: positions[i], text: l }))} xGrid={false} height="h-20" {positions} bind:at bind:hover
  label={v.caption === 'today' ? t('activitiesLast7Days') : t('activitiesEachDayThisWeek')} valueText={reading || t('activitiesNoDayPicked')}>
  {#snippet overlay()}
    <Bars bars={v.bars.map((b, i) => ({ at: positions[i], v: b * (axis?.factor ?? 1), color: b > 0 ? 'var(--theme-accent)' : 'var(--theme-surface2)' }))}
      width={0.6 / n} lo={scale.lo} hi={scale.hi} picked={at} />
  {/snippet}
</Chart>
